import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import DataTable from "../../../components/common/DataTable";
import PrimaryCell from "../../../components/common/PrimaryCell";
import EmployeePicker from "../../users/components/EmployeePicker";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetDepartmentsQuery } from "../../masters/departments/departmentsApi";
import { useGetHrDesignationsQuery } from "../../masters/designations/hrDesignationsApi";
import { useCanMarkLeave } from "../useLeavePermissions";
import MusterRollModal from "../components/MusterRollModal";
import LeaveFormPrintModal from "../components/LeaveFormPrintModal";
import {
  useLazyGetLeaveMonthCalcQuery,
  useGetLeaveTimelineQuery,
  useMarkLeaveMutation,
  useGetLeaveEligibilitySuggestionsQuery,
} from "../leaveApi";

// Default target month is NEXT month, not the current one - per direct
// instruction, HR plans a month ahead (in September they enter October's
// leave), matching how the Month picker below drives the whole list.
const nextMonthValue = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtMonthYear = (v) => new Date(v).toLocaleDateString(undefined, { month: "short", year: "numeric" });

// Same 3-tier green/amber/red pill CSS already established for the AI
// Guard Roster's candidate cards (guard-candidate-status-pill / status-
// recommended|review|not-eligible) - reused as-is for the consistency
// score, rather than a parallel set of colors for the same "good/middling/
// bad" idea.
const CONSISTENCY_STATUS_META = {
  GOOD: { label: "Good", cls: "status-recommended" },
  AVERAGE: { label: "Average", cls: "status-review" },
  POOR: { label: "Poor", cls: "status-not-eligible" },
};

// Web port of the legacy WinForms frm_Leave (single employee) + Frm_Bulk_leave
// (department-wide batch) screens. Originally shipped as two separate lists
// (a full "browse everyone this month" grid plus a narrower "who's due"
// panel) - merged into ONE list per direct instruction (2026-09-07,
// Batch131): the due-for-leave list, now filterable by target Month/Year
// (default next month), Department, Designation and Search, with the DOP/
// Pending Leave/Already-Marked columns folded in from the retired grid.
// Picking one employee (checkbox, row click, or the code/name jump-to
// search) drives the same holiday-calculated Mark Leave form and (single-
// employee only) Previous Leave Timeline below - both paths write the same
// shape of row to the same table (Batch129_LeaveMaster.sql's
// App_LeaveMaster), synced down into each branch's own legacy Leave_Master
// unchanged.
const LeaveManagementPage = () => {
  const canMark = useCanMarkLeave();
  const { selectedBranchId } = useBranchFilter();
  const { data: departments = [] } = useGetDepartmentsQuery();
  const { data: designations = [] } = useGetHrDesignationsQuery();

  const [monthValue, setMonthValue] = useState(nextMonthValue());
  const [year, month] = monthValue.split("-").map(Number);
  const [departmentId, setDepartmentId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const [musterRollEmployeeId, setMusterRollEmployeeId] = useState(null);

  // Two independent selection concepts: checkedGuids (grid checkboxes, bulk
  // mark) and focusedEmployee (a single row clicked, or picked via the
  // code/name search) - checking a box always wins for what the Mark Leave
  // form below targets, since "several employees selected" only makes sense
  // for the bulk path; the Previous Leave Timeline only ever makes sense
  // for exactly one employee, so it's tied to focusedEmployee alone.
  const [checkedGuids, setCheckedGuids] = useState(new Set());
  const [focusedEmployee, setFocusedEmployee] = useState(null); // { employeeRecordGuid, empName, empLastName, empCode }

  const { data: suggestions = [], isFetching: isLoadingSuggestions } = useGetLeaveEligibilitySuggestionsQuery({
    branchId: selectedBranchId ?? undefined,
    departmentId: departmentId || undefined,
    designationId: designationId || undefined,
    search: search || undefined,
    year,
    month,
  });

  // "Jump to employee should fetch his data even if it is not in the list"
  // (direct instruction) - a picked/focused employee may not match the
  // current due-window/branch/department/designation/search filters, so
  // this fetches that ONE employee's row directly (server bypasses every
  // filter but branch access when employeeRecordGuid is supplied - see
  // Batch131) and it gets merged into the displayed rows below. Skipped
  // once the employee is already present in the filtered list, so picking
  // a row that's already showing doesn't fire a redundant request.
  const focusedAlreadyInList =
    !!focusedEmployee && suggestions.some((s) => s.employeeRecordGuid === focusedEmployee.employeeRecordGuid);
  const { data: focusedRows } = useGetLeaveEligibilitySuggestionsQuery(
    { year, month, employeeRecordGuid: focusedEmployee?.employeeRecordGuid },
    { skip: !focusedEmployee?.employeeRecordGuid || focusedAlreadyInList }
  );

  // Merge by employee, never by concatenation: the focused row is fetched while
  // the employee is NOT in the list, and the hook keeps that result after a later
  // search brings the same employee into the list - concatenating then showed
  // him twice (2026-09-27, "duplicate rows for the same employee in leave").
  const rows = useMemo(() => {
    if (!focusedRows || focusedRows.length === 0) return suggestions;
    const seen = new Set();
    return [...focusedRows, ...suggestions].filter((r) => {
      if (seen.has(r.employeeRecordGuid)) return false;
      seen.add(r.employeeRecordGuid);
      return true;
    });
  }, [suggestions, focusedRows]);

  useEffect(() => {
    setCheckedGuids(new Set());
    setFocusedEmployee(null);
  }, [selectedBranchId, departmentId, designationId, year, month]);

  const targetGuids = checkedGuids.size > 0 ? [...checkedGuids] : focusedEmployee ? [focusedEmployee.employeeRecordGuid] : [];
  // Prefer the enriched row from `rows` (has branchCode/designationName -
  // needed for the Leave Form print) over the bare focusedEmployee object
  // set by handlePickEmployee's "jump to employee" path - falls back to
  // focusedEmployee only in the brief window before its own row has loaded.
  const targetEmployees =
    checkedGuids.size > 0
      ? rows.filter((r) => checkedGuids.has(r.employeeRecordGuid))
      : focusedEmployee
      ? [rows.find((r) => r.employeeRecordGuid === focusedEmployee.employeeRecordGuid) || focusedEmployee]
      : [];

  const [fromDate, setFromDate] = useState(`${monthValue}-01`);
  useEffect(() => {
    setFromDate(`${monthValue}-01`);
  }, [monthValue]);
  const [reason, setReason] = useState("");
  // Surrender (Batch169): the employee keeps working and is paid for the
  // leave instead. Saved and synced like any leave; the pay itself is made in
  // the Windows salary screen (Leave Pay, "Surrender only") - nothing is
  // calculated here.
  const [surrender, setSurrender] = useState(false);

  // holidays_master's real data is per-branch (verified live on-prem
  // 2026-09-05, not the documented-as-global BranchId=0 sentinel alone), so
  // the preview needs a real branchId - the focused/first-checked
  // employee's own branch when known, else the header's branch filter.
  const calcBranchId = targetEmployees[0]?.branchId ?? selectedBranchId;
  const [fetchCalc, { data: calc, isFetching: isCalculating }] = useLazyGetLeaveMonthCalcQuery();
  useEffect(() => {
    if (!fromDate || !calcBranchId) return;
    const [y, m] = fromDate.split("-").map(Number);
    fetchCalc({ year: y, month: m, branchId: calcBranchId });
  }, [fromDate, calcBranchId, fetchCalc]);

  const toDatePreview = useMemo(() => {
    if (!fromDate || calc == null) return null;
    const d = new Date(fromDate);
    d.setDate(d.getDate() + calc.totalDays);
    return d;
  }, [fromDate, calc]);

  const { data: timeline = [], isFetching: isLoadingTimeline } = useGetLeaveTimelineQuery(focusedEmployee?.employeeRecordGuid, {
    skip: !focusedEmployee || checkedGuids.size > 0,
  });

  const [markLeave, { isLoading: isSaving }] = useMarkLeaveMutation();
  const [printSlips, setPrintSlips] = useState(null);

  const handleRowClick = (record) => {
    setCheckedGuids(new Set());
    setFocusedEmployee(record);
  };

  const handlePickEmployee = (option) => {
    if (!option) return;
    setCheckedGuids(new Set());
    setFocusedEmployee({
      employeeRecordGuid: option.value,
      empCode: option.employee?.empCode,
      empName: option.employee?.empName,
      empLastName: option.employee?.empLastName,
      branchId: option.employee?.branchId,
    });
  };

  const handleSave = async () => {
    if (targetGuids.length === 0) {
      toast.error("Select at least one employee");
      return;
    }
    try {
      const result = await markLeave({ employeeRecordGuids: targetGuids, fromDate, reason: reason.trim() || undefined, surrender }).unwrap();
      const succeeded = result.results.filter((r) => r.success);
      const skipped = result.results.filter((r) => !r.success);
      const who = `${succeeded.length} employee${succeeded.length > 1 ? "s" : ""}`;
      if (succeeded.length > 0) {
        toast.success(
          surrender
            ? `Leave surrender marked for ${who}. Pay it from the Windows salary screen (Leave Pay, Surrender only).`
            : `Leave marked for ${who}.`,
          surrender ? { duration: 7000 } : undefined
        );
      }
      skipped.forEach((s) => toast.error(s.skipReason || "Could not mark leave for one employee."));
      setReason("");
      setCheckedGuids(new Set());
      const wasSurrender = surrender;
      setSurrender(false);
      // A surrendered employee isn't going anywhere, so there is no Leave Form to sign.
      if (wasSurrender) return;

      // Employee Leave Form print, one slip per employee who actually got
      // marked (direct instruction, 2026-09-07) - identity/designation come
      // from targetEmployees (the enriched row, not the raw markLeave
      // response, which only carries ToDate/Days); ToDate is per-employee
      // since holidays_master is per-branch (a bulk save spanning branches
      // can legitimately compute a different day count for each).
      if (succeeded.length > 0) {
        const byId = new Map(targetEmployees.map((e) => [e.employeeRecordGuid, e]));
        const slips = succeeded.map((r) => {
          const emp = byId.get(r.employeeRecordGuid) || {};
          return {
            branchCode: emp.branchCode,
            empCode: emp.empCode,
            empName: emp.empName,
            empLastName: emp.empLastName,
            designationName: emp.designationName,
            year: new Date(fromDate).getFullYear(),
            fromDate: fmtDate(fromDate),
            toDate: fmtDate(r.toDate),
          };
        });
        setPrintSlips(slips);
      }
    } catch (error) {
      toast.error(error?.message || "Could not mark leave.");
    }
  };

  const columns = [
    // The employee's name leads, the code sits under it (2026-09-12).
    {
      title: "Employee",
      dataIndex: "empName",
      render: (v, r) => <PrimaryCell primary={`${r.empName || ""} ${r.empLastName || ""}`.trim()} secondary={r.empCode} />,
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "DOP", dataIndex: "empDop", render: fmtDate },
    {
      title: "Pending Leave",
      dataIndex: "pendingLeaveMonths",
      render: (v) =>
        v ? (
          <div className="d-flex flex-wrap gap-1">
            {v.split(", ").map((m) => (
              <span key={m} className="badge badge-linewarning">
                {m}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    { title: "Last Leave", dataIndex: "lastLeaveFromDate", render: fmtDate },
    { title: "Due Date", dataIndex: "dueDate", render: fmtDate },
    {
      title: "Status",
      dataIndex: "alreadyMarkedThisMonth",
      render: (v) => (v ? <span className="badge badge-linesuccess">Marked this month</span> : <span className="text-muted">-</span>),
    },
    {
      title: "Consistency",
      dataIndex: "consistencyScore",
      render: (v, r) => {
        const meta = CONSISTENCY_STATUS_META[r.consistencyStatus] || CONSISTENCY_STATUS_META.POOR;
        return (
          <span
            className={`guard-candidate-status-pill ${meta.cls}`}
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              setMusterRollEmployeeId(r.employeeRecordGuid);
            }}
          >
            {v} - {meta.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Leave Management</h4>
              <h6>Who's due for leave, holiday-calculated - for one employee or many at once</h6>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row g-2 align-items-end mb-3">
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Month</label>
                <input type="month" className="form-control" value={monthValue} onChange={(e) => setMonthValue(e.target.value)} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Department</label>
                <select className="form-control" value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">All departments</option>
                  {departments.map((d) => (
                    <option key={d.departmentCode} value={d.departmentCode}>
                      {d.departmentName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">Designation</label>
                <select className="form-control" value={designationId} onChange={(e) => setDesignationId(e.target.value)}>
                  <option value="">All designations</option>
                  {designations.map((d) => (
                    <option key={d.designationCode} value={d.designationCode}>
                      {d.designationName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name or code"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">Jump to employee</label>
                <EmployeePicker value={null} onChange={handlePickEmployee} />
              </div>
            </div>

            <p className="text-muted small mb-2">
              Due within +/-1 month of the selected month and not yet on leave that calendar year. Advisory only - click a
              score for the 12-month muster roll.
            </p>

            <DataTable
              columns={columns}
              dataSource={rows}
              rowKey="employeeRecordGuid"
              loading={isLoadingSuggestions}
              onRow={(record) => ({ onClick: () => handleRowClick(record), style: { cursor: "pointer" } })}
              rowSelection={
                canMark
                  ? {
                      selectedRowKeys: [...checkedGuids],
                      onChange: (keys) => {
                        setFocusedEmployee(null);
                        setCheckedGuids(new Set(keys));
                      },
                    }
                  : undefined
              }
            />
          </div>
        </div>

        {canMark && targetGuids.length > 0 && (
          <div className="card mt-3">
            <div className="card-body">
              <h5 className="mb-3">
                {targetEmployees.length === 1
                  ? `Mark Leave - ${targetEmployees[0].empName || ""} ${targetEmployees[0].empLastName || ""} (#${targetEmployees[0].empCode})`
                  : `Mark Leave - ${targetEmployees.length} employees selected`}
              </h5>
              <div className="row g-2 align-items-end mb-2">
                <div className="col-6 col-md-3">
                  <label className="form-label small text-muted mb-1">From Date</label>
                  <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div className="col-12 col-md-5">
                  {isCalculating && <div className="small text-muted">Calculating...</div>}
                  {!isCalculating && calc && (
                    <div className="small text-muted">
                      26 base + {calc.holidayCount} holiday{calc.holidayCount === 1 ? "" : "s"} + {calc.sundayCount} Sunday
                      {calc.sundayCount === 1 ? "" : "s"} = <strong>{calc.totalDays} days</strong>
                      {toDatePreview && <> (through {fmtDate(toDatePreview)})</>}
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <label className="form-label small text-muted mb-1">Reason</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Optional"
                  // The branch Leave table holds 50 characters (the API refuses more).
                  maxLength={50}
                />
              </div>
              <div className="form-check mb-3">
                <input
                  id="leaveSurrender"
                  type="checkbox"
                  className="form-check-input"
                  checked={surrender}
                  onChange={(e) => setSurrender(e.target.checked)}
                />
                <label htmlFor="leaveSurrender" className="form-check-label">
                  Surrender - the employee keeps working and is paid for this leave instead
                </label>
                {surrender && (
                  <div className="small text-muted mt-1">
                    No leave allowance is added. The surrender pay and payslip are made in the Windows salary screen
                    (tick Leave Pay, then Surrender only) once this has synced to the branch.
                  </div>
                )}
              </div>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : surrender ? "Save Surrender" : "Save"}
              </button>
            </div>
          </div>
        )}

        {focusedEmployee && checkedGuids.size === 0 && (
          <div className="card mt-3">
            <div className="card-body">
              <h6 className="mb-3">Previous Leave Timeline</h6>
              {isLoadingTimeline && <p className="text-muted small">Loading...</p>}
              {!isLoadingTimeline && timeline.length === 0 && <p className="text-muted small">No eligibility history yet.</p>}
              <div className="d-flex flex-column gap-2">
                {timeline.map((t) => (
                  <div
                    key={t.eligibilityDate}
                    className="d-flex justify-content-between align-items-center border-start border-3 border-primary ps-3"
                  >
                    <span>{fmtMonthYear(t.eligibilityDate)}</span>
                    {t.recordGuid && t.surrender ? (
                      <span className="badge badge-lineinfo">Surrendered {fmtMonthYear(t.fromDate)}</span>
                    ) : t.recordGuid ? (
                      <span className="badge badge-linesuccess">
                        Taken {fmtDate(t.fromDate)} - {fmtDate(t.toDate)} ({t.days} days)
                      </span>
                    ) : (
                      <span className="badge badge-linewarning">Pending</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {musterRollEmployeeId && (
        <MusterRollModal employeeRecordGuid={musterRollEmployeeId} onClose={() => setMusterRollEmployeeId(null)} />
      )}

      {printSlips && <LeaveFormPrintModal show slips={printSlips} onClose={() => setPrintSlips(null)} />}
    </div>
  );
};

export default LeaveManagementPage;
